import type { ChangeEvent } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import type { InputProps } from '@polyai/ui/components/atoms/Input';

import HeadlineInput from 'components/atoms/HeadlineInput';
import * as Styled from './InputStyles.styled';
import type { SharedFormProps } from './types';

type Props<T> = SharedFormProps<T> &
  InputProps & {
    inline?: boolean;
    isHeadline?: boolean;
    allowAutofill?: boolean;
    readonlySuffix?: string;
  };

export const FormTextInput = <T,>({
  controlKey,
  inline = false,
  onChange,
  isHeadline = false,
  portal = false,
  allowAutofill = false,
  readonlySuffix,
  ...rest
}: Props<T>) => {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={controlKey as string}
      render={({ field, fieldState: { error } }) => {
        const handleChange = (
          event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          const rawValue = event.target.value;
          // Keep the value as a string for validation purposes
          // The schema expects string values even for number inputs
          field.onChange(rawValue);
          if (onChange) {
            onChange(event);
          }
        };
        // https://developer.mozilla.org/en-US/docs/Web/Security/Practical_implementation_guides/Turning_off_form_autocompletion#managing_autofill_for_login_fields
        const autoCompleteProps: React.InputHTMLAttributes<HTMLInputElement> =
          allowAutofill === false ? { autoComplete: 'new-password' } : {};
        const inputProps = {
          ...field,
          errors: !!error ? [error.message || ''] : [],
          onChange: handleChange,
          ...autoCompleteProps,
          ...rest,
          portal,
          readonlySuffix,
        };
        if (isHeadline) {
          return <HeadlineInput {...inputProps} />;
        }
        return <Styled.FormTextInput $inline={inline} {...inputProps} />;
      }}
    />
  );
};
